import * as React from 'react'
import { apiClient } from '@jetkit/react'
import { clearAuthTokens } from 'axios-jwt'
import { useStoreActions } from '../store'

/**
 * Whether a failed request proves the current session is finished.
 *
 * It has to be a 401 *on a request that actually carried credentials*. axios-jwt attaches
 * no Authorization header when there is no refresh token to attach, so an anonymous call
 * coming back 401 says nothing at all about the signed-in user -- and this client makes
 * plenty of them: GeneralOptionsProvider fetches /country and /language on every mount,
 * sign-in page included, which is most of the ~175 unauthenticated 401s a day.
 *
 * Treating those as proof was a way to be thrown out of a session you had just started.
 * The /country request issued while the sign-in page was still on screen could land after
 * the login had completed, and tore down the user and the tokens that had just replaced
 * it -- so a correct password put you on the dashboard for a moment and then back on the
 * sign-in page, with nothing to show why.
 */
export const endsSession = (error: any): boolean => {
  if (!error || !error.response || error.response.status !== 401) return false

  const headers = (error.config && error.config.headers) || {}
  return Boolean(headers.Authorization || headers.authorization)
}

/**
 * Clears the session when the API rejects our credentials.
 *
 * Installs itself and ejects on unmount. It used to hand back a `setupInterceptor`
 * function that App called from an effect with no cleanup, so a fresh handler was
 * registered on every render of App and never removed -- and every one of them then ran
 * on a single 401.
 */
export const useApiErrorInterceptor = () => {
  const removeCurrentUser = useStoreActions((actions) => actions.user.removeCurrent)

  React.useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (endsSession(error)) {
          // Deliberately no history.push. App mounts its own BrowserRouter *below* this
          // hook, so the history reachable here belongs to the outer router: pushing it
          // moved the address bar without navigating the UI that Routes renders. Clearing
          // the user is what actually navigates, via PrivateRoute. That lands everyone on
          // /sign-in, which is no longer a dead end now that every sign-in page accepts
          // every role.
          removeCurrentUser()
          clearAuthTokens()
        }
        return Promise.reject(error)
      }
    )

    return () => {
      apiClient.interceptors.response.eject(interceptorId)
    }
  }, [removeCurrentUser])
}
